import { createSelector, createSlice, current, isDraft, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ProposalCollaborationEnvelope,
  ProposalConflict,
  ProposalContentOperation,
  ProposalResolutionState,
  ProposalReviewerChange,
  SaveProposalDraftCommand,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  createProposalCollaborationState,
  failProposalSave,
  queueProposalOperation,
  queueProposalReviewerChange,
  rebasePendingProposalContentOperations,
  dropUnappliableProposalCollectionUpdates,
  receiveProposalEnvelope,
  recordProposalConflicts,
  settleProposalClientTeamRoster,
  stageProposalClientTeamRoster,
  startProposalSave,
  type ProposalCollaborationState,
} from "work/proposals/negotiation/proposal-collaboration-state";
import {
  authoritativeProposalClientTeamRoster,
  proposalClientTeamRosterIsDirty,
  proposalClientTeamRosterMovedUnderEdit,
  type ProposalClientTeamMemberReference,
  type ProposalClientTeamRosterDraft,
} from "work/proposals/negotiation/proposal-client-team-roster";
import {
  projectProposalOperations,
  projectProposalResolutionStates,
  projectProposalReviewerChanges,
} from "work/proposals/negotiation/proposal-operation-projector";
import {
  emptyProposalCollisionKeys,
  proposalCollisionKeys,
  type ProposalCollisionKeys,
} from "work/proposals/negotiation/proposal-field-collision";

export type ProposalCollaborationStoreState = {
  activeViewerScope?: string;
  byNegotiationId: Record<string, ProposalCollaborationState | undefined>;
  hydratedNegotiationIds: Record<string, true | undefined>;
  negotiationIdByProposalId: Record<string, string | undefined>;
};

const initialState: ProposalCollaborationStoreState = {
  activeViewerScope: undefined,
  byNegotiationId: {},
  hydratedNegotiationIds: {},
  negotiationIdByProposalId: {},
};

type ScopedProposalEnvelope = {
  envelope: ProposalCollaborationEnvelope;
  viewerScope: string;
};

function readEnvelopePayload(payload: ProposalCollaborationEnvelope | ScopedProposalEnvelope) {
  return "envelope" in payload
    ? payload
    : { envelope: payload, viewerScope: undefined };
}

function activateViewerScope(state: ProposalCollaborationStoreState, viewerScope: string): void {
  if (state.activeViewerScope === viewerScope) return;
  state.activeViewerScope = viewerScope;
  state.byNegotiationId = {};
  state.hydratedNegotiationIds = {};
  state.negotiationIdByProposalId = {};
}

function unwrapEntry(entry: ProposalCollaborationState | undefined): ProposalCollaborationState {
  if (!entry) return createProposalCollaborationState();
  return isDraft(entry) ? (current(entry) as ProposalCollaborationState) : entry;
}

const proposalCollaborationSlice = createSlice({
  name: "proposalCollaboration",
  initialState,
  reducers: {
    proposalViewerScopeActivated: (state, action: PayloadAction<{ viewerScope: string }>) => {
      activateViewerScope(state, action.payload.viewerScope);
    },
    proposalEnvelopeReceived: (
      state,
      action: PayloadAction<ProposalCollaborationEnvelope | ScopedProposalEnvelope>,
    ) => {
      const { envelope, viewerScope } = readEnvelopePayload(action.payload);
      if (viewerScope) {
        if (state.activeViewerScope && state.activeViewerScope !== viewerScope) return;
        activateViewerScope(state, viewerScope);
      }
      const { negotiationId } = envelope;
      const received = receiveProposalEnvelope(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        envelope,
      );
      state.byNegotiationId[negotiationId] = received;
      const proposalId = received.envelope?.projection.id;
      if (proposalId) state.negotiationIdByProposalId[proposalId] = negotiationId;
    },
    proposalOperationQueued: (
      state,
      action: PayloadAction<{ negotiationId: string; operation: ProposalContentOperation }>,
    ) => {
      const { negotiationId, operation } = action.payload;
      state.byNegotiationId[negotiationId] = queueProposalOperation(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        operation,
      );
    },
    /**
     * Batches N operations into the single reducer pass `proposalOperationQueued` would otherwise
     * take N dispatches to achieve. The persistence middleware runs once per *action*, doing a full
     * parse + Zod re-validate + stringify of the whole durable-queue blob, so N single-operation
     * dispatches from one `queueProposalUpdate` call (a blur/flatten routinely queues a dozen field
     * operations at once) previously cost N full read-modify-writes. One action, one write.
     */
    proposalOperationsQueued: (
      state,
      action: PayloadAction<{ negotiationId: string; operations: readonly ProposalContentOperation[] }>,
    ) => {
      const { negotiationId, operations } = action.payload;
      let entry = unwrapEntry(state.byNegotiationId[negotiationId]);
      for (const operation of operations) {
        entry = queueProposalOperation(entry, operation);
      }
      state.byNegotiationId[negotiationId] = entry;
    },
    proposalReviewerChangeQueued: (
      state,
      action: PayloadAction<{ negotiationId: string; reviewerChange: ProposalReviewerChange }>,
    ) => {
      const { negotiationId, reviewerChange } = action.payload;
      state.byNegotiationId[negotiationId] = queueProposalReviewerChange(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        reviewerChange,
      );
    },
    /** Batched sibling of `proposalReviewerChangeQueued` — see that action's batched counterpart above. */
    proposalReviewerChangesQueued: (
      state,
      action: PayloadAction<{ negotiationId: string; reviewerChanges: readonly ProposalReviewerChange[] }>,
    ) => {
      const { negotiationId, reviewerChanges } = action.payload;
      let entry = unwrapEntry(state.byNegotiationId[negotiationId]);
      for (const reviewerChange of reviewerChanges) {
        entry = queueProposalReviewerChange(entry, reviewerChange);
      }
      state.byNegotiationId[negotiationId] = entry;
    },
    /**
     * Holds the client-team roster the viewer has edited, against the roster the server held when
     * they started editing. Nothing is written here — the explicit Save is what sends the replace
     * command (#5116).
     */
    proposalClientTeamMembersStaged: (
      state,
      action: PayloadAction<{
        negotiationId: string;
        members: readonly ProposalClientTeamMemberReference[];
        baseline: readonly ProposalClientTeamMemberReference[];
      }>,
    ) => {
      const { negotiationId, members, baseline } = action.payload;
      state.byNegotiationId[negotiationId] = stageProposalClientTeamRoster(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        { members: [...members], baseline: [...baseline] },
      );
    },
    /**
     * A `client-team-members` write has been acknowledged. `members` is the roster it carried, and
     * only that roster is settled: an edit staged while the request was in flight survives as
     * unsaved work rather than being discarded with it (#5276).
     */
    proposalClientTeamMembersSaved: (
      state,
      action: PayloadAction<{ negotiationId: string; members: readonly ProposalClientTeamMemberReference[] }>,
    ) => {
      const { negotiationId, members } = action.payload;
      state.byNegotiationId[negotiationId] = settleProposalClientTeamRoster(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        members,
      );
    },
    proposalPendingContentOperationsRebased: (
      state,
      action: PayloadAction<{
        negotiationId: string;
        conflictKeys: string[];
        operations: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>[];
      }>,
    ) => {
      const { negotiationId, conflictKeys, operations } = action.payload;
      state.byNegotiationId[negotiationId] = rebasePendingProposalContentOperations(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        conflictKeys,
        operations,
      );
    },
    /**
     * The viewer chose to refresh and re-apply their changes after a save was refused for an update
     * to an item the proposal does not hold (#6612). Runs after the refreshed envelope is received.
     */
    proposalUnappliableUpdatesDropped: (state, action: PayloadAction<{ negotiationId: string }>) => {
      const { negotiationId } = action.payload;
      state.byNegotiationId[negotiationId] = dropUnappliableProposalCollectionUpdates(
        unwrapEntry(state.byNegotiationId[negotiationId]),
      );
    },
    proposalSaveStarted: (
      state,
      action: PayloadAction<{ negotiationId: string; command: SaveProposalDraftCommand; viewerScope?: string }>,
    ) => {
      const { negotiationId, command, viewerScope } = action.payload;
      if (viewerScope) {
        if (state.activeViewerScope && state.activeViewerScope !== viewerScope) return;
        activateViewerScope(state, viewerScope);
      }
      state.byNegotiationId[negotiationId] = startProposalSave(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        command,
      );
    },
    proposalSaveFailed: (
      state,
      action: PayloadAction<{
        negotiationId: string;
        commandId: string;
        viewerScope?: string;
        uncertain?: boolean;
        conflicts?: ProposalConflict[];
      }>,
    ) => {
      const { negotiationId, commandId, viewerScope, uncertain, conflicts } = action.payload;
      if (viewerScope && state.activeViewerScope !== viewerScope) return;
      state.byNegotiationId[negotiationId] = failProposalSave(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        commandId,
        { uncertain, conflicts },
      );
    },
    proposalConflictsReported: (
      state,
      action: PayloadAction<{ negotiationId: string; viewerScope?: string; conflicts: ProposalConflict[] }>,
    ) => {
      const { negotiationId, viewerScope, conflicts } = action.payload;
      if (viewerScope && state.activeViewerScope !== viewerScope) return;
      state.byNegotiationId[negotiationId] = recordProposalConflicts(
        unwrapEntry(state.byNegotiationId[negotiationId]),
        conflicts,
      );
    },
    proposalLocalChangesDiscarded: (state, action: PayloadAction<{ negotiationId: string }>) => {
      const { negotiationId } = action.payload;
      const existing = unwrapEntry(state.byNegotiationId[negotiationId]);
      state.byNegotiationId[negotiationId] = createProposalCollaborationState({
        envelope: existing.envelope,
        conflicts: existing.envelope?.conflicts ?? [],
      });
    },
    proposalLocalQueueHydrated: (
      state,
      action: PayloadAction<{
        negotiationId: string;
        viewerScope?: string;
        pendingOperations: ProposalContentOperation[];
        pendingReviewerChanges: ProposalReviewerChange[];
        inFlightCommands: Record<string, SaveProposalDraftCommand | undefined>;
      }>,
    ) => {
      const { negotiationId, viewerScope, pendingOperations, pendingReviewerChanges, inFlightCommands } =
        action.payload;
      if (viewerScope) {
        if (state.activeViewerScope && state.activeViewerScope !== viewerScope) return;
        activateViewerScope(state, viewerScope);
      }
      const existing = unwrapEntry(state.byNegotiationId[negotiationId]);
      const hydrated = createProposalCollaborationState({
        ...existing,
        pendingOperations,
        pendingReviewerChanges,
        inFlightCommands,
        syncStatus: Object.keys(inFlightCommands).length > 0 ? "resyncRequired" : existing.syncStatus,
      });
      const reconciled = existing.envelope
        ? receiveProposalEnvelope(hydrated, existing.envelope)
        : hydrated;
      state.byNegotiationId[negotiationId] = {
        ...reconciled,
        // Any command that was not acknowledged by the first envelope still
        // has an uncertain outcome after a browser restart. Force the hook's
        // idempotent refetch/retry path instead of leaving it stuck as saving.
        syncStatus: Object.keys(reconciled.inFlightCommands).length > 0
          ? "resyncRequired"
          : reconciled.syncStatus,
      };
      state.hydratedNegotiationIds[negotiationId] = true;
    },
    proposalResyncRequested: (state, action: PayloadAction<{ negotiationId: string }>) => {
      const entry = state.byNegotiationId[action.payload.negotiationId];
      if (entry) entry.syncStatus = "resyncRequired";
    },
    allProposalCollaborationsResyncRequested: (state) => {
      for (const entry of Object.values(state.byNegotiationId)) {
        if (entry) entry.syncStatus = "resyncRequired";
      }
    },
    /**
     * Optimistically advances the viewer's seen cursor for a path (the "" key is the whole proposal) so a
     * field's unread change badge clears the instant its history opens, ahead of the envelope refetch the
     * markProposalChangesSeen mutation triggers. A cursor is only ever moved forward.
     */
    proposalChangesSeenLocally: (
      state,
      action: PayloadAction<{ negotiationId: string; path: string; throughStreamVersion: number }>,
    ) => {
      const { negotiationId, path, throughStreamVersion } = action.payload;
      const entry = state.byNegotiationId[negotiationId];
      if (!entry?.envelope) return;

      const key = path || "";
      const cursors = { ...(entry.envelope.viewerSeenThroughStreamVersion ?? {}) };
      if ((cursors[key] ?? 0) >= throughStreamVersion) return;
      cursors[key] = throughStreamVersion;
      entry.envelope.viewerSeenThroughStreamVersion = cursors;
      if (key === "") {
        entry.envelope.viewerSeenAllThroughStreamVersion = Math.max(
          entry.envelope.viewerSeenAllThroughStreamVersion ?? 0,
          throughStreamVersion,
        );
      }
    },
  },
});

export const {
  proposalViewerScopeActivated,
  proposalEnvelopeReceived,
  proposalOperationQueued,
  proposalOperationsQueued,
  proposalReviewerChangeQueued,
  proposalReviewerChangesQueued,
  proposalClientTeamMembersStaged,
  proposalClientTeamMembersSaved,
  proposalPendingContentOperationsRebased,
  proposalUnappliableUpdatesDropped,
  proposalSaveStarted,
  proposalSaveFailed,
  proposalConflictsReported,
  proposalLocalChangesDiscarded,
  proposalLocalQueueHydrated,
  proposalResyncRequested,
  allProposalCollaborationsResyncRequested,
  proposalChangesSeenLocally,
} = proposalCollaborationSlice.actions;

type ProposalCollaborationRootState = {
  proposalCollaboration: ProposalCollaborationStoreState;
};

const selectEntry = (state: ProposalCollaborationRootState, negotiationId: string) =>
  state.proposalCollaboration.byNegotiationId[negotiationId];

export const selectProposalCollaborationEntry = (negotiationId: string) =>
  (state: ProposalCollaborationRootState) => selectEntry(state, negotiationId);

export const selectProposalCollaborationViewerScope = (state: ProposalCollaborationRootState) =>
  state.proposalCollaboration.activeViewerScope;

export const selectProposalLocalQueueHydrated = (negotiationId: string, viewerScope: string) =>
  (state: ProposalCollaborationRootState) =>
    state.proposalCollaboration.activeViewerScope === viewerScope &&
    state.proposalCollaboration.hydratedNegotiationIds[negotiationId] === true;

export const selectProposalNegotiationIdByProposalId = (proposalId: string) =>
  (state: ProposalCollaborationRootState) => state.proposalCollaboration.negotiationIdByProposalId[proposalId];

export const selectProposalEnvelope = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry) => entry?.envelope,
  );

export const selectEffectiveProposalJSON = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry) => {
      if (!entry?.envelope) return undefined;
      const inFlightCommands = Object.values(entry.inFlightCommands).filter(
        (command): command is SaveProposalDraftCommand => Boolean(command),
      );
      const contentOperations = [
        ...inFlightCommands.flatMap((command) => command.operations),
        ...entry.pendingOperations,
      ];
      const reviewerChanges = [
        ...inFlightCommands.flatMap((command) => command.reviewerChanges),
        ...entry.pendingReviewerChanges,
      ];
      const contentProjection = projectProposalOperations(entry.envelope.projection, contentOperations);
      return projectProposalReviewerChanges(contentProjection, entry.envelope.viewingParty, reviewerChanges);
    },
  );

export const selectEffectiveProposalResolutionStates = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry): ProposalResolutionState[] => {
      if (!entry?.envelope) return [];
      const inFlightCommands = Object.values(entry.inFlightCommands).filter(
        (command): command is SaveProposalDraftCommand => Boolean(command),
      );
      return projectProposalResolutionStates(
        entry.envelope.resolutionStates,
        [
          ...inFlightCommands.flatMap((command) => command.operations),
          ...entry.pendingOperations,
        ],
      );
    },
  );

/**
 * The roster the server holds for this negotiation, leader excluded — the base every staged
 * client-team edit is measured and rebased against.
 */
export const selectAuthoritativeProposalClientTeamRoster = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry): ProposalClientTeamMemberReference[] =>
      authoritativeProposalClientTeamRoster(entry?.envelope?.projection.clientTeam),
  );

export const selectProposalClientTeamRosterDraft = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry): ProposalClientTeamRosterDraft | undefined => entry?.pendingClientTeamMembers,
  );

/** Whether an unsaved client-team roster edit is waiting for the explicit Save (#5116). */
export const selectProposalHasPendingClientTeamMembers = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry) =>
      proposalClientTeamRosterIsDirty(
        entry?.pendingClientTeamMembers,
        authoritativeProposalClientTeamRoster(entry?.envelope?.projection.clientTeam),
      ),
  );

/**
 * Whether a teammate's roster change landed under this viewer's unsaved client-team edit. The edit
 * is rebased onto it rather than dropped, so this only decides whether the tab says so.
 */
export const selectProposalClientTeamRosterMovedUnderEdit = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry) =>
      proposalClientTeamRosterIsDirty(
        entry?.pendingClientTeamMembers,
        authoritativeProposalClientTeamRoster(entry?.envelope?.projection.clientTeam),
      ) &&
      proposalClientTeamRosterMovedUnderEdit(
        entry?.pendingClientTeamMembers,
        authoritativeProposalClientTeamRoster(entry?.envelope?.projection.clientTeam),
      ),
  );

/**
 * Whether the editor is holding unsaved proposal content or reviewer changes — the negotiated work
 * a submission would publish.
 *
 * Deliberately narrower than {@link selectProposalHasPendingChanges}: a staged client-team roster
 * is unsaved work, but it is reference state written by its own command and is never negotiated
 * content (`docs/design/proposals-negotiation/ARCHITECTURE.md`). Read the wider question to decide
 * whether there is anything to save; read this one to decide whether there is anything to submit,
 * or a roster-only edit would arm Submit and send the counterparty a submission with nothing in it
 * to negotiate (#5276).
 */
export const selectProposalHasPendingContentChanges = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry) =>
      Boolean(
        entry &&
          (entry.pendingOperations.length > 0 ||
            entry.pendingReviewerChanges.length > 0 ||
            Object.keys(entry.inFlightCommands).length > 0),
      ),
  );

/**
 * Whether the editor is holding anything an explicit Save would write.
 *
 * The staged client-team roster counts even though it is not a content operation: it is written by
 * the same Save press, so a page that read it as clean would leave the Save disabled over work the
 * viewer can see on the roster in front of them, and would let them navigate away from it
 * unwarned (#5116). It is only ever the answer to "is there unsaved work?" — "is there something to
 * submit?" is {@link selectProposalHasPendingContentChanges}.
 */
export const selectProposalHasPendingChanges = (negotiationId: string) =>
  createSelector(
    [
      (state: ProposalCollaborationRootState) => selectEntry(state, negotiationId),
      selectProposalHasPendingContentChanges(negotiationId),
    ],
    (entry, hasPendingContentChanges) =>
      Boolean(
        entry &&
          (hasPendingContentChanges ||
            proposalClientTeamRosterIsDirty(
              entry.pendingClientTeamMembers,
              authoritativeProposalClientTeamRoster(entry.envelope?.projection.clientTeam),
            )),
      ),
  );

export const selectProposalConflicts = (negotiationId: string) =>
  createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry) => entry?.conflicts ?? [],
  );

export type ProposalPendingChanges = {
  operations: readonly ProposalContentOperation[];
  reviewerChanges: readonly ProposalReviewerChange[];
};

const noProposalPendingChanges: ProposalPendingChanges = { operations: [], reviewerChanges: [] };

const proposalPendingChangesSelectors = new Map<string, (state: ProposalCollaborationRootState) => ProposalPendingChanges>();

/**
 * Exactly what a Save is about to persist: the queued content operations plus the queued reviewer
 * changes. The Save confirmation describes these rather than re-deriving a change log from the legacy
 * builder's session history, so what the dialog promises and what the command carries cannot diverge.
 *
 * Memoized per negotiation — the confirmation reads it on a page that re-renders constantly.
 */
export const selectProposalPendingChanges = (negotiationId: string) => {
  const existing = proposalPendingChangesSelectors.get(negotiationId);
  if (existing) return existing;

  const selector = createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry): ProposalPendingChanges => {
      if (!entry) return noProposalPendingChanges;
      if (entry.pendingOperations.length === 0 && entry.pendingReviewerChanges.length === 0) {
        return noProposalPendingChanges;
      }
      return { operations: entry.pendingOperations, reviewerChanges: entry.pendingReviewerChanges };
    },
  );

  proposalPendingChangesSelectors.set(negotiationId, selector);
  return selector;
};

function buildProposalCollisionSelector(negotiationId: string) {
  return createSelector(
    [(state: ProposalCollaborationRootState) => selectEntry(state, negotiationId)],
    (entry): ProposalCollisionKeys => {
      if (!entry?.envelope) return emptyProposalCollisionKeys;
      const inFlightCommands = Object.values(entry.inFlightCommands).filter(
        (command): command is SaveProposalDraftCommand => Boolean(command),
      );
      const localOperations = [
        ...inFlightCommands.flatMap((command) => command.operations),
        ...entry.pendingOperations,
      ];
      // The envelope carries both halves of the acknowledgement: the viewer's seen cursors and when
      // each path last changed. Opening a field's history advances the cursor, which is what lets an
      // acknowledged collision stop sounding the alarm.
      return proposalCollisionKeys(entry.envelope.projection, localOperations, entry.envelope);
    },
  );
}

// One memoized selector per negotiation, cached so every field's history button shares a single
// three-way merge (which clones the projection) instead of each recomputing it on every render.
const proposalCollisionSelectors = new Map<string, ReturnType<typeof buildProposalCollisionSelector>>();

export const selectProposalCollisions = (negotiationId: string) => {
  const existing = proposalCollisionSelectors.get(negotiationId);
  if (existing) return existing;
  const selector = buildProposalCollisionSelector(negotiationId);
  proposalCollisionSelectors.set(negotiationId, selector);
  return selector;
};

export default proposalCollaborationSlice;
